import { useState, useEffect, useRef } from "react";
import { toast } from "sonner";
import axios from "axios";
import { Link } from "react-router-dom";
import { MapPin, Home as HomeIcon, FileText, Briefcase, Building2, ChevronLeft, ChevronRight, CheckCircle2, ShieldCheck } from "lucide-react";
import { SEO } from "../components/SEO";
import { API } from "../lib/constants";

const PROFILES = [
  { id: "frontalier", icon: MapPin, label: "Je suis frontalier Suisse-France" },
  { id: "resident", icon: HomeIcon, label: "Je suis résident suisse" },
  { id: "francaise", icon: FileText, label: "Je souhaite faire une déclaration fiscale française" },
  { id: "independant", icon: Briefcase, label: "Je suis indépendant en Suisse" },
  { id: "entreprise", icon: Building2, label: "Je représente une entreprise en Suisse" },
];

const FISCAL_QUESTIONS = {
  frontalier: [
    { key: "employeur_suisse", label: "Avez-vous un employeur en Suisse ?", type: "bool" },
    { key: "imposition_source", label: "Êtes-vous imposé à la source ?", type: "bool" },
    { key: "rectification_source", label: "Besoin d'une rectification d'impôt à la source ?", type: "bool" },
    { key: "revenus_suisse", label: "Revenus en Suisse (par an)", type: "money", currency: "CHF" },
    { key: "revenus_france", label: "Revenus en France (par an)", type: "money", currency: "EUR" },
    { key: "frais_garde", label: "Avez-vous des frais de garde ?", type: "bool" },
    { key: "frais_deplacement", label: "Avez-vous des frais de déplacement ?", type: "bool" },
    { key: "assurance_maladie", label: "Payez-vous une assurance maladie (LAMal/CMU) ?", type: "bool" },
    { key: "credit_immobilier", label: "Avez-vous un crédit immobilier ?", type: "bool" },
    { key: "biens_immobiliers", label: "Possédez-vous des biens immobiliers ?", type: "bool" },
    { key: "revenus_locatifs", label: "Percevez-vous des revenus locatifs ?", type: "bool" },
    { key: "comptes_etranger", label: "Avez-vous des comptes bancaires à l'étranger ?", type: "bool" },
    { key: "aide_declaration_francaise", label: "Besoin d'aide pour la déclaration française ?", type: "bool" },
    { key: "aide_declaration_suisse", label: "Besoin d'aide pour la déclaration suisse ?", type: "bool" },
  ],
  resident: [
    { key: "revenus_salaries", label: "Revenus salariés (par an)", type: "money", currency: "CHF" },
    { key: "revenus_independants", label: "Revenus indépendants (par an)", type: "money", currency: "CHF" },
    { key: "fortune", label: "Fortune approximative", type: "money", currency: "CHF" },
    { key: "immobilier", label: "Possédez-vous de l'immobilier ?", type: "bool" },
    { key: "charges_deductibles", label: "Avez-vous des charges déductibles (3e pilier, rachats LPP…) ?", type: "bool" },
    { key: "assurance_maladie", label: "Primes d'assurance maladie à déduire ?", type: "bool" },
    { key: "enfants_charge", label: "Nombre d'enfants à charge", type: "text" },
    { key: "type_prestation", label: "Besoin de vérification ou déclaration complète ?", type: "select", options: ["Simple vérification", "Déclaration complète"] },
  ],
  francaise: [
    { key: "type_revenus", label: "Type de revenus (salaires, retraite, indépendant…)", type: "text" },
    { key: "revenus_francais", label: "Revenus français (par an)", type: "money", currency: "EUR" },
    { key: "revenus_suisses", label: "Revenus suisses (par an)", type: "money", currency: "CHF" },
    { key: "revenus_fonciers", label: "Percevez-vous des revenus fonciers ?", type: "bool" },
    { key: "credit_immobilier", label: "Avez-vous un crédit immobilier ?", type: "bool" },
    { key: "frais_reels", label: "Déclarez-vous des frais réels ?", type: "bool" },
    { key: "deductions_fiscales", label: "Avez-vous des déductions fiscales (dons, emploi à domicile…) ?", type: "bool" },
    { key: "personnes_foyer", label: "Nombre de personnes dans le foyer", type: "text" },
  ],
  independant: [
    { key: "domaine_activite", label: "Domaine d'activité", type: "text" },
    { key: "chiffre_affaires", label: "Chiffre d'affaires estimé (par an)", type: "money", currency: "CHF" },
    { key: "charges_professionnelles", label: "Charges professionnelles estimées (par an)", type: "money", currency: "CHF" },
    { key: "tva", label: "Êtes-vous assujetti à la TVA ?", type: "bool" },
    { key: "comptabilite_tenue", label: "Votre comptabilité est-elle déjà tenue ?", type: "bool" },
    { key: "aide_declaration", label: "Besoin d'aide pour la déclaration fiscale ?", type: "bool" },
    { key: "accompagnement_annuel", label: "Besoin d'un accompagnement annuel ?", type: "bool" },
  ],
  entreprise: [
    { key: "forme_juridique", label: "Forme juridique", type: "select", options: ["Sàrl", "SA", "Raison individuelle", "Société en nom collectif", "Autre"] },
    { key: "chiffre_affaires", label: "Chiffre d'affaires (par an)", type: "money", currency: "CHF" },
    { key: "nombre_employes", label: "Nombre d'employés", type: "text" },
    { key: "tva", label: "Êtes-vous assujetti à la TVA ?", type: "bool" },
    { key: "comptabilite_existante", label: "Avez-vous une comptabilité existante ?", type: "bool" },
    { key: "accompagnement_fiscal", label: "Besoin d'un accompagnement fiscal ?", type: "bool" },
    { key: "suivi_annuel", label: "Besoin d'un suivi annuel ?", type: "bool" },
    { key: "optimisation_fiscale", label: "Besoin d'optimisation fiscale ?", type: "bool" },
  ],
};

const formatMoney = (v) => {
  const digits = String(v).replace(/[^\d]/g, "").slice(0, 12);
  if (!digits) return "";
  return digits.replace(/\B(?=(\d{3})+(?!\d))/g, "'");
};

const getDocumentsList = (profile, personal, fiscal) => {
  if (profile === "entreprise" || profile === "independant") {
    const docs = [
      "Bilan et compte de résultat",
      "Comptabilité existante / grand livre",
      "Relevés bancaires professionnels",
      "Factures et justificatifs de charges professionnelles",
    ];
    if (profile === "entreprise") docs.push("Extrait du registre du commerce", "Statuts de la société");
    if (profile === "independant") docs.push("Décomptes de cotisations AVS indépendant");
    if (fiscal.tva === "oui") docs.push("Décomptes TVA");
    if (profile === "entreprise" && parseInt(String(fiscal.nombre_employes || "").replace(/\D/g, "") || "0") > 0) docs.push("Décomptes AVS et certificats de salaire des employés");
    docs.push("Aucun document pour le moment");
    return docs;
  }
  const docs = [
    "Certificat de salaire suisse et attestation-quittance",
    "Identifiants e-démarches ou avis d'imposition",
    "Attestation 3e pilier A et/ou rachat de 2e pilier",
    "Attestation fiscale d'assurance maladie (LAMal/CMU et complémentaires)",
    "Attestation assurance vie / 3e pilier B",
    "Frais de tenue de comptes bancaires",
    "Frais médicaux non remboursés par l'assurance",
    "Justificatifs de dons (organismes avec siège en Suisse)",
    "Frais de formation, perfectionnement ou reconversion",
    "Autres revenus (chômage, allocations familiales, rentes…)",
  ];
  if (profile === "francaise") docs.unshift("Attestation fiscale française / dernier avis d'imposition");
  if (personal.enfants === "avec enfants") docs.push("Frais de garde (factures ou justificatifs)");
  const hasProperty = fiscal.biens_immobiliers === "oui" || fiscal.immobilier === "oui" || fiscal.revenus_fonciers === "oui";
  if (hasProperty) {
    docs.push(
      "Intérêts hypothécaires (attestation récapitulative annuelle)",
      "Taxe foncière recto-verso",
      "Assurance habitation / multirisque",
      "Frais d'entretien du bien et charges de copropriété"
    );
  }
  if (fiscal.credit_immobilier === "oui") docs.push("Attestation de crédit immobilier et assurance emprunt");
  if (fiscal.revenus_locatifs === "oui") docs.push("Justificatifs des loyers encaissés (bail, récapitulatif)");
  if (fiscal.comptes_etranger === "oui") docs.push("Relevés des comptes bancaires à l'étranger");
  docs.push("Aucun document pour le moment");
  return docs;
};

const ACCOMPAGNEMENTS = [
  "Simulation", "Simple vérification", "Déclaration complète", "Rectification fiscale",
  "Conseil fiscal ponctuel", "Accompagnement annuel", "Urgence fiscale",
];

const SITUATIONS = ["Célibataire", "Marié(e)", "Pacsé(e)", "Divorcé(e)", "Veuf/Veuve", "Concubin(e)"];

const STEP_TITLES = ["Type de profil", "Informations personnelles", "Situation fiscale", "Avez-vous ces documents ?", "Accompagnement souhaité", "Votre message", "Consentement"];

const STEP_SUBTITLES = {
  3: "Pour obtenir une estimation précise (personnelle ou professionnelle), cochez les documents que vous possédez déjà.",
  4: "Plusieurs choix possibles.",
};

const inputCls = "w-full text-sm px-4 py-3 rounded-xl border border-[#C4C8CC]/70 bg-white focus:outline-none focus:border-[#0A1128] transition-colors duration-200";
const labelCls = "block text-sm font-semibold text-[#0A1128] mb-1.5";

const BoolField = ({ label, value, onChange, testId }) => (
  <div data-testid={testId}>
    <p className={labelCls}>{label}</p>
    <div className="flex gap-2">
      {[["oui", "Oui"], ["non", "Non"]].map(([v, l]) => (
        <button
          key={v} type="button"
          data-testid={`${testId}-${v}`}
          onClick={() => onChange(v)}
          className={`px-6 py-2 rounded-full text-sm font-medium border transition-colors duration-200 ${
            value === v ? "bg-[#0A1128] text-white border-[#0A1128]" : "bg-white text-[#4B5563] border-[#C4C8CC]/70 hover:border-[#0A1128]"
          }`}
        >
          {l}
        </button>
      ))}
    </div>
  </div>
);

const computeEstimate = (profile, personal, fiscal, accompaniment) => {
  if (accompaniment.length === 1 && accompaniment[0] === "Simulation") {
    return { label: "Gratuit", detail: "La simulation est offerte, sans engagement." };
  }
  let base, cur;
  if (profile === "francaise") { base = 100; cur = "EUR"; }
  else if (profile === "independant") { base = 300; cur = "CHF"; }
  else if (profile === "entreprise") { base = 500; cur = "CHF"; }
  else { base = 100; cur = "CHF"; }
  if (["Marié(e)", "Pacsé(e)"].includes(personal.situation_familiale)) base += 20;
  if (personal.enfants === "avec enfants") base += 20;
  if (fiscal.biens_immobiliers === "oui" || fiscal.immobilier === "oui" || fiscal.revenus_fonciers === "oui") base += 20;
  if (fiscal.revenus_locatifs === "oui") base += 20;
  if (fiscal.comptes_etranger === "oui") base += 20;
  if (fiscal.rectification_source === "oui" || accompaniment.includes("Rectification fiscale")) base += 50;
  if (fiscal.aide_declaration_francaise === "oui" && fiscal.aide_declaration_suisse === "oui") base += 100;
  if (accompaniment.includes("Conseil fiscal ponctuel")) base += 100;
  if (accompaniment.includes("Accompagnement annuel")) base += 150;
  if (accompaniment.includes("Urgence fiscale")) base += 50;
  const high = Math.ceil((base * 1.5) / 10) * 10;
  const fmt = (n) => String(n).replace(/\B(?=(\d{3})+(?!\d))/g, "'");
  const label = cur === "EUR" ? `${fmt(base)} € – ${fmt(high)} €` : `CHF ${fmt(base)}.- – CHF ${fmt(high)}.-`;
  return { label, detail: null };
};

export default function Devis() {
  const [step, setStep] = useState(0);
  const [submitted, setSubmitted] = useState(false);
  const [loading, setLoading] = useState(false);
  const [profile, setProfile] = useState("");
  const [personal, setPersonal] = useState({
    nom: "", prenom: "", email: "", telephone: "", pays_residence: "", canton: "", commune: "",
    situation_familiale: "", enfants: "", raison_sociale: "",
  });
  const [fiscal, setFiscal] = useState({});
  const [documents, setDocuments] = useState([]);
  const [accompaniment, setAccompaniment] = useState([]);
  const [message, setMessage] = useState("");
  const [consent, setConsent] = useState(false);
  const [estimateResult, setEstimateResult] = useState(null);
  const [website, setWebsite] = useState("");
  const [startedAt] = useState(() => Date.now());
  const [captchaToken, setCaptchaToken] = useState("");
  const captchaRef = useRef(null);
  const widgetIdRef = useRef(null);
  const SITEKEY = process.env.REACT_APP_TURNSTILE_SITEKEY;

  useEffect(() => {
    if (step !== 6 || !SITEKEY) return;
    let cancelled = false;
    const renderWidget = () => {
      if (cancelled || !window.turnstile || !captchaRef.current || widgetIdRef.current !== null) return;
      widgetIdRef.current = window.turnstile.render(captchaRef.current, {
        sitekey: SITEKEY,
        theme: "light",
        action: "devis_form",
        callback: (t) => setCaptchaToken(t),
        "expired-callback": () => setCaptchaToken(""),
        "error-callback": () => setCaptchaToken(""),
      });
    };
    const existing = document.querySelector("script[data-turnstile]");
    if (existing) {
      if (window.turnstile) renderWidget();
      else existing.addEventListener("load", renderWidget);
    } else {
      const script = document.createElement("script");
      script.src = "https://challenges.cloudflare.com/turnstile/v0/api.js?render=explicit";
      script.async = true;
      script.defer = true;
      script.dataset.turnstile = "true";
      script.onload = renderWidget;
      document.head.appendChild(script);
    }
    return () => {
      cancelled = true;
      if (widgetIdRef.current !== null && window.turnstile) {
        window.turnstile.remove(widgetIdRef.current);
        widgetIdRef.current = null;
        setCaptchaToken("");
      }
    };
  }, [step, SITEKEY]);

  const setP = (k, v) => setPersonal((p) => ({ ...p, [k]: v }));
  const setF = (k, v) => setFiscal((f) => ({ ...f, [k]: v }));

  const validate = () => {
    if (step === 0 && !profile) { toast.error("Veuillez sélectionner votre profil."); return false; }
    if (step === 1) {
      if (!personal.nom || !personal.prenom || !personal.email) { toast.error("Nom, prénom et email sont obligatoires."); return false; }
      if (profile === "entreprise" && !personal.raison_sociale) { toast.error("La raison sociale est obligatoire."); return false; }
      if (!/^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(personal.email)) { toast.error("Adresse email invalide."); return false; }
    }
    if (step === 4 && accompaniment.length === 0) { toast.error("Veuillez choisir au moins un type d'accompagnement."); return false; }
    return true;
  };

  const next = () => { if (validate()) setStep((s) => Math.min(s + 1, 6)); };
  const prev = () => setStep((s) => Math.max(s - 1, 0));

  const submit = async () => {
    if (!consent) { toast.error("Le consentement est obligatoire pour envoyer votre demande."); return; }
    if (SITEKEY && !captchaToken) { toast.error("Veuillez valider la vérification anti-robot."); return; }
    setLoading(true);
    const est = computeEstimate(profile, personal, fiscal, accompaniment);
    try {
      await axios.post(`${API}/quotes`, {
        profile_type: profile,
        personal,
        fiscal,
        documents,
        accompaniment,
        estimate: est.label,
        message,
        consent,
        website,
        form_seconds: (Date.now() - startedAt) / 1000,
        captcha_token: captchaToken,
      });
      setEstimateResult(est);
      setSubmitted(true);
    } catch (err) {
      if (err.response?.status === 429) {
        toast.error(err.response.data?.detail || "Trop de demandes envoyées. Veuillez réessayer plus tard.");
      } else if (err.response?.status === 400 && typeof err.response.data?.detail === "string") {
        toast.error(err.response.data.detail);
      } else {
        toast.error("Une erreur est survenue lors de l'envoi. Veuillez réessayer.");
      }
      if (widgetIdRef.current !== null && window.turnstile) {
        window.turnstile.reset(widgetIdRef.current);
        setCaptchaToken("");
      }
    }
    setLoading(false);
  };

  if (submitted) {
    return (
      <main data-testid="devis-success" className="pt-[76px] min-h-[80vh] flex items-center bg-[#F8F9FA]">
        <SEO title="Demande envoyée | Fiscoconseils" description="Votre demande de devis a bien été envoyée à Fiscoconseils." noindex />
        <div className="max-w-2xl mx-auto px-5 py-24 text-center">
          <div className="w-20 h-20 rounded-full bg-[#0A1128] flex items-center justify-center mx-auto">
            <CheckCircle2 size={38} strokeWidth={1.5} className="text-[#C4C8CC]" />
          </div>
          <h1 className="font-heading text-4xl sm:text-5xl font-semibold tracking-tight text-[#0A1128] mt-8">
            Votre demande a bien été envoyée
          </h1>
          <p className="text-base text-[#4B5563] mt-5 leading-relaxed">
            Un conseiller Fiscoconseils analysera votre situation et vous recontactera rapidement avec une proposition adaptée.
          </p>
          {estimateResult && (
            <div data-testid="devis-estimate" className="mt-8 bg-white border border-[#C4C8CC]/50 rounded-2xl p-8 shadow-[0_8px_32px_rgba(10,17,40,0.06)]">
              <p className="text-xs tracking-[0.2em] uppercase font-bold text-[#6B7280]">Estimation tarifaire indicative</p>
              <p data-testid="devis-estimate-value" className="font-heading text-3xl sm:text-4xl text-[#0A1128] mt-3">{estimateResult.label}</p>
              {estimateResult.detail && <p className="text-sm text-[#4B5563] mt-2">{estimateResult.detail}</p>}
              <p className="text-xs text-[#9CA3AF] mt-4 leading-relaxed">
                Fourchette calculée selon vos réponses, à titre purement indicatif. Le devis final sera établi et confirmé par un conseiller Fiscoconseils après analyse complète de votre situation.
              </p>
            </div>
          )}
          <Link to="/" data-testid="devis-back-home" className="inline-block mt-10 text-sm font-semibold text-white bg-[#0A1128] rounded-full px-8 py-4 hover:bg-[#1A2342] transition-colors duration-200">
            Retour à l'accueil
          </Link>
        </div>
      </main>
    );
  }

  return (
    <main data-testid="devis-page" className="pt-[76px] bg-[#F8F9FA] min-h-screen">
      <SEO
        title="Demander un devis fiscal personnalisé | Fiscoconseils"
        description="Demandez un devis personnalisé : déclaration fiscale frontalier suisse, déclaration impôts Suisse France, fiscalité indépendant et entreprise suisse. Gratuit et sans engagement."
      />
      <div className="max-w-3xl mx-auto px-5 py-16">
        <p className="text-xs tracking-[0.2em] uppercase font-bold text-[#6B7280] mb-3">Devis personnalisé — gratuit et sans engagement</p>
        <h1 className="font-heading text-4xl sm:text-5xl font-semibold tracking-tight text-[#0A1128]">Demander un devis</h1>

        {/* Progress */}
        <div className="flex items-center gap-1.5 mt-10">
          {STEP_TITLES.map((title, i) => (
            <div key={title} className={`h-1.5 flex-1 rounded-full transition-colors duration-300 ${i <= step ? "bg-[#0A1128]" : "bg-[#C4C8CC]/50"}`} />
          ))}
        </div>
        <p className="text-sm font-semibold text-[#0A1128] mt-4">Étape {step + 1} / 7 — {STEP_TITLES[step]}</p>

        <div className="bg-white border border-[#C4C8CC]/50 rounded-2xl shadow-[0_8px_32px_rgba(10,17,40,0.06)] p-7 sm:p-10 mt-6">
          <input
            type="text"
            name="website"
            value={website}
            onChange={(e) => setWebsite(e.target.value)}
            tabIndex={-1}
            autoComplete="off"
            aria-hidden="true"
            style={{ position: "absolute", left: "-9999px", height: 0, width: 0, opacity: 0 }}
          />
          <div className="mb-8">
            <h2 data-testid="step-card-title" className="font-heading text-2xl sm:text-3xl tracking-tight text-[#0A1128]">{STEP_TITLES[step]}</h2>
            {STEP_SUBTITLES[step] && <p className="text-sm text-[#6B7280] mt-2 leading-relaxed">{STEP_SUBTITLES[step]}</p>}
          </div>
          {step === 0 && (
            <div className="space-y-3">
              {PROFILES.map((p) => (
                <button
                  key={p.id} type="button"
                  data-testid={`profile-${p.id}`}
                  onClick={() => setProfile(p.id)}
                  className={`w-full flex items-center gap-4 text-left px-5 py-4 rounded-xl border-2 transition-colors duration-200 ${
                    profile === p.id ? "border-[#0A1128] bg-[#0A1128] text-white" : "border-[#C4C8CC]/60 hover:border-[#0A1128]/50"
                  }`}
                >
                  <p.icon size={22} strokeWidth={1.5} className={`shrink-0 ${profile === p.id ? "text-white" : "text-[#0A1128]"}`} />
                  <span className={`flex-1 text-sm font-semibold ${profile === p.id ? "text-white" : "text-[#0A1128]"}`}>{p.label}</span>
                  {profile === p.id && <CheckCircle2 size={20} strokeWidth={2} className="text-[#C9A961] shrink-0" />}
                </button>
              ))}
            </div>
          )}

          {step === 1 && (
            <div className="grid grid-cols-1 sm:grid-cols-2 gap-5">
              {profile === "entreprise" && (
                <div className="sm:col-span-2"><label className={labelCls}>Raison sociale de l'entreprise *</label><input data-testid="input-raison-sociale" className={inputCls} value={personal.raison_sociale} onChange={(e) => setP("raison_sociale", e.target.value)} placeholder="Ex. : Fiscoconseils Sàrl" /></div>
              )}
              <div><label className={labelCls}>{profile === "entreprise" ? "Nom du contact *" : "Nom *"}</label><input data-testid="input-nom" className={inputCls} value={personal.nom} onChange={(e) => setP("nom", e.target.value)} /></div>
              <div><label className={labelCls}>{profile === "entreprise" ? "Prénom du contact *" : "Prénom *"}</label><input data-testid="input-prenom" className={inputCls} value={personal.prenom} onChange={(e) => setP("prenom", e.target.value)} /></div>
              <div><label className={labelCls}>Adresse email *</label><input data-testid="input-email" type="email" className={inputCls} value={personal.email} onChange={(e) => setP("email", e.target.value)} /></div>
              <div><label className={labelCls}>Téléphone</label><input data-testid="input-telephone" className={inputCls} value={personal.telephone} onChange={(e) => setP("telephone", e.target.value)} /></div>
              {profile !== "entreprise" && (
                <div><label className={labelCls}>Pays de résidence</label>
                  <select data-testid="input-pays" className={inputCls} value={personal.pays_residence} onChange={(e) => setP("pays_residence", e.target.value)}>
                    <option value="">Sélectionner…</option><option>France</option><option>Suisse</option><option>Autre</option>
                  </select></div>
              )}
              <div><label className={labelCls}>{profile === "entreprise" ? "Canton du siège social" : "Canton de travail / résidence en Suisse"}</label>
                <select data-testid="input-canton" className={inputCls} value={personal.canton} onChange={(e) => setP("canton", e.target.value)}>
                  <option value="">Sélectionner…</option>
                  {["Genève", "Vaud", "Valais", "Neuchâtel", "Jura", "Berne", "Fribourg", "Bâle-Ville", "Bâle-Campagne", "Zurich", "Autre canton"].map((c) => <option key={c}>{c}</option>)}
                </select></div>
              <div><label className={labelCls}>{profile === "entreprise" ? "Commune du siège" : "Commune"}</label><input data-testid="input-commune" className={inputCls} value={personal.commune} onChange={(e) => setP("commune", e.target.value)} /></div>
              {profile !== "entreprise" && (
                <>
                  <div><label className={labelCls}>Situation familiale</label>
                    <select data-testid="input-situation" className={inputCls} value={personal.situation_familiale} onChange={(e) => setP("situation_familiale", e.target.value)}>
                      <option value="">Sélectionner…</option>
                      {SITUATIONS.map((s) => <option key={s}>{s}</option>)}
                    </select></div>
                  <div className="sm:col-span-2">
                    <BoolField label="Avez-vous des enfants ?" testId="input-enfants" value={personal.enfants === "avec enfants" ? "oui" : personal.enfants === "sans enfants" ? "non" : ""} onChange={(v) => setP("enfants", v === "oui" ? "avec enfants" : "sans enfants")} />
                  </div>
                </>
              )}
            </div>
          )}

          {step === 2 && (
            <div className="space-y-6">
              {(FISCAL_QUESTIONS[profile] || []).map((q) => (
                q.type === "bool" ? (
                  <BoolField key={q.key} label={q.label} testId={`fiscal-${q.key}`} value={fiscal[q.key] || ""} onChange={(v) => setF(q.key, v)} />
                ) : q.type === "select" ? (
                  <div key={q.key}>
                    <label className={labelCls}>{q.label}</label>
                    <select data-testid={`fiscal-${q.key}`} className={inputCls} value={fiscal[q.key] || ""} onChange={(e) => setF(q.key, e.target.value)}>
                      <option value="">Sélectionner…</option>
                      {q.options.map((o) => <option key={o}>{o}</option>)}
                    </select>
                  </div>
                ) : q.type === "money" ? (
                  <div key={q.key}>
                    <label className={labelCls}>{q.label}</label>
                    <div className="relative">
                      <input
                        data-testid={`fiscal-${q.key}`}
                        className={`${inputCls} pr-20`}
                        inputMode="numeric"
                        placeholder={q.currency === "EUR" ? "100'000" : "100'000"}
                        value={fiscal[q.key] || ""}
                        onChange={(e) => setF(q.key, formatMoney(e.target.value))}
                      />
                      <span className="absolute right-4 top-1/2 -translate-y-1/2 text-sm font-semibold text-[#6B7280] pointer-events-none">
                        {q.currency === "EUR" ? "€" : "CHF"}
                      </span>
                    </div>
                  </div>
                ) : (
                  <div key={q.key}>
                    <label className={labelCls}>{q.label}</label>
                    <input data-testid={`fiscal-${q.key}`} className={inputCls} value={fiscal[q.key] || ""} onChange={(e) => setF(q.key, e.target.value)} />
                  </div>
                )
              ))}
            </div>
          )}

          {step === 3 && (
            <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
              {getDocumentsList(profile, personal, fiscal).map((d, i) => (
                <label key={d} data-testid={`doc-${i}`} className={`flex items-center gap-3 px-4 py-3.5 rounded-xl border cursor-pointer transition-colors duration-200 ${documents.includes(d) ? "border-[#0A1128] bg-[#0A1128]/[0.04]" : "border-[#C4C8CC]/60 hover:border-[#0A1128]/50"}`}>
                  <input
                    type="checkbox"
                    checked={documents.includes(d)}
                    onChange={() => setDocuments((docs) => docs.includes(d) ? docs.filter((x) => x !== d) : [...docs, d])}
                    className="accent-[#0A1128] w-4 h-4"
                  />
                  <span className="text-sm font-medium text-[#0A1128]">{d}</span>
                </label>
              ))}
            </div>
          )}

          {step === 4 && (
            <div className="space-y-3">
              {ACCOMPAGNEMENTS.map((a, i) => (
                <button
                  key={a} type="button"
                  data-testid={`accomp-${i}`}
                  onClick={() => setAccompaniment((sel) => sel.includes(a) ? sel.filter((x) => x !== a) : [...sel, a])}
                  className={`w-full flex items-center justify-between text-left px-5 py-4 rounded-xl border text-sm font-semibold text-[#0A1128] transition-colors duration-200 ${
                    accompaniment.includes(a) ? "border-[#0A1128] bg-[#0A1128]/[0.04]" : "border-[#C4C8CC]/60 hover:border-[#0A1128]/50"
                  }`}
                >
                  {a}
                  {accompaniment.includes(a) && <span className="text-xs font-bold text-[#0A1128]">✓ Sélectionné</span>}
                </button>
              ))}
            </div>
          )}

          {step === 5 && (
            <div>
              <label className={labelCls}>Expliquez brièvement votre situation.</label>
              <textarea
                data-testid="input-message"
                rows={6}
                className={inputCls}
                value={message}
                onChange={(e) => setMessage(e.target.value)}
                placeholder="Décrivez votre situation fiscale, vos questions ou vos délais…"
              />
            </div>
          )}

          {step === 6 && (
            <div>
              <label data-testid="consent-label" className="flex items-start gap-3 cursor-pointer">
                <input
                  data-testid="consent-checkbox"
                  type="checkbox"
                  checked={consent}
                  onChange={(e) => setConsent(e.target.checked)}
                  className="accent-[#0A1128] w-5 h-5 mt-0.5"
                />
                <span className="text-sm text-[#374151] leading-relaxed">
                  J'accepte que Fiscoconseils me contacte concernant ma demande et confirme avoir pris connaissance de la{" "}
                  <Link to="/politique-de-confidentialite" className="underline text-[#0A1128]">politique de confidentialité</Link>. *
                </span>
              </label>
              <div className="flex items-center gap-2 mt-6 text-xs text-[#6B7280]">
                <ShieldCheck size={15} strokeWidth={1.75} /> Vos données sont transmises de manière sécurisée et traitées confidentiellement.
              </div>
              {SITEKEY && (
                <div className="mt-6">
                  <p className="text-sm font-semibold text-[#0A1128] mb-2">Vérification anti-robot *</p>
                  <div ref={captchaRef} data-testid="turnstile-widget" />
                </div>
              )}
            </div>
          )}

          <div className="flex items-center justify-between mt-10">
            <button
              data-testid="devis-prev-btn"
              type="button"
              onClick={prev}
              disabled={step === 0}
              className="inline-flex items-center gap-2 text-sm font-semibold text-[#4B5563] px-5 py-3 rounded-full border border-[#C4C8CC]/70 hover:bg-[#F8F9FA] transition-colors duration-200 disabled:opacity-40"
            >
              <ChevronLeft size={16} /> Précédent
            </button>
            {step < 6 ? (
              <button
                data-testid="devis-next-btn"
                type="button"
                onClick={next}
                className="inline-flex items-center gap-2 text-sm font-semibold text-white bg-[#0A1128] rounded-full px-7 py-3 hover:bg-[#1A2342] transition-colors duration-200"
              >
                Suivant <ChevronRight size={16} />
              </button>
            ) : (
              <button
                data-testid="devis-submit-btn"
                type="button"
                onClick={submit}
                disabled={loading}
                className="inline-flex items-center gap-2 text-sm font-semibold text-white bg-[#0A1128] rounded-full px-8 py-3 hover:bg-[#1A2342] transition-colors duration-200 disabled:opacity-60"
              >
                {loading ? "Envoi en cours…" : "Envoyer ma demande"}
              </button>
            )}
          </div>
        </div>
      </div>
    </main>
  );
}
